import { useState, useEffect } from 'react';
import { IconFlask, IconCloudUpload, IconArrowsSort, IconLoader2 } from '@tabler/icons-react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface ContactPreview {
  email: string;
  FIRSTNAME: string;
  LASTNAME: string;
  SPAR_MOBILE_DC: string;
  SPAR_MOBILE_ROM: string;
  SPAR_MOBILE_STORE: string;
  ROLE: string;
}

interface PreviewResponse {
  dc: ContactPreview[];
  rom: ContactPreview[];
  store: ContactPreview[];
}

interface RemoteContactStatus {
  email: string;
  inLocal: boolean;
}

interface RemoteStatusResponse {
  dc: RemoteContactStatus[];
  rom: RemoteContactStatus[];
  store: RemoteContactStatus[];
  onlyInDb?: {
    dc: string[];
    rom: string[];
    store: string[];
  };
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

function ContactTable({ title, contacts, search }: { title: string; contacts: ContactPreview[]; search: string }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [search]);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredContacts = normalizedSearch
    ? contacts.filter((c) => {
        const haystack = [c.email, c.FIRSTNAME, c.LASTNAME, c.SPAR_MOBILE_DC, c.SPAR_MOBILE_ROM, c.SPAR_MOBILE_STORE, c.ROLE]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(normalizedSearch);
      })
    : contacts;

  const pagedContacts =
    rowsPerPage > 0
      ? filteredContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : filteredContacts;

  const totalContacts = contacts.length;
  const uniqueEmailCount = new Set(
    contacts.map((c) => c.email?.trim().toLowerCase()).filter((e): e is string => !!e),
  ).size;
  const totalPages = Math.ceil(filteredContacts.length / rowsPerPage);

  return (
    <AccordionItem value={slugify(title)}>
      <AccordionTrigger className="text-sm font-semibold">
        {title} — {totalContacts} contact{totalContacts !== 1 ? 's' : ''} ({uniqueEmailCount} unique email
        {uniqueEmailCount !== 1 ? 's' : ''})
      </AccordionTrigger>
      <AccordionContent>
        {contacts.length === 0 ? (
          <p className="text-muted-foreground">No contacts for this level.</p>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>SPAR Mobile DC</TableHead>
                    <TableHead>SPAR Mobile ROM</TableHead>
                    <TableHead>SPAR Mobile Store</TableHead>
                    <TableHead>Role</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedContacts.map((c, i) => (
                    <TableRow key={`${c.email}-${i}`}>
                      <TableCell>
                        <span className="block max-w-[200px] truncate" title={c.email}>{c.email}</span>
                      </TableCell>
                      <TableCell>{c.FIRSTNAME || '—'}</TableCell>
                      <TableCell>{c.LASTNAME || '—'}</TableCell>
                      <TableCell>
                        {c.SPAR_MOBILE_DC ? (
                          <span className="block max-w-[150px] truncate" title={c.SPAR_MOBILE_DC}>{c.SPAR_MOBILE_DC}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {c.SPAR_MOBILE_ROM ? (
                          <span className="block max-w-[200px] truncate" title={c.SPAR_MOBILE_ROM}>{c.SPAR_MOBILE_ROM}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>
                        {c.SPAR_MOBILE_STORE ? (
                          <span className="block max-w-[200px] truncate" title={c.SPAR_MOBILE_STORE}>{c.SPAR_MOBILE_STORE}</span>
                        ) : '—'}
                      </TableCell>
                      <TableCell>{c.ROLE}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between px-2 py-3">
              <span className="text-sm text-muted-foreground">
                {filteredContacts.length} result{filteredContacts.length !== 1 ? 's' : ''}
              </span>
              <div className="flex items-center gap-2">
                <select
                  className="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                >
                  {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
                </select>
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
                <span className="text-sm">Page {page + 1} of {totalPages || 1}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function RemoteContactsTable({ title, contacts }: { title: string; contacts: RemoteContactStatus[] }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = [...contacts].sort((a, b) => {
    const ea = a.email.toLowerCase();
    const eb = b.email.toLowerCase();
    if (ea < eb) return sortAsc ? -1 : 1;
    if (ea > eb) return sortAsc ? 1 : -1;
    return 0;
  });

  const paged = rowsPerPage > 0 ? sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sorted;
  const total = contacts.length;
  const inLocalCount = contacts.filter((c) => c.inLocal).length;
  const onlyBrevoCount = total - inLocalCount;
  const totalPages = Math.ceil(contacts.length / rowsPerPage);

  return (
    <AccordionItem value={`${slugify(title)}-remote`}>
      <AccordionTrigger className="text-sm font-semibold">
        {title} — {total} contact{total !== 1 ? 's' : ''} ({inLocalCount} in DB, {onlyBrevoCount} only in Brevo)
      </AccordionTrigger>
      <AccordionContent>
        {contacts.length === 0 ? (
          <p className="text-muted-foreground">No contacts currently in this Brevo list.</p>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button className="flex items-center gap-1" onClick={() => setSortAsc(!sortAsc)}>
                        Email <IconArrowsSort className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>In DB?</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((c, i) => (
                    <TableRow
                      key={`${c.email}-${i}`}
                      className={cn(!c.inLocal && 'bg-red-50 border-l-4 border-l-destructive')}
                    >
                      <TableCell>
                        <span className="block max-w-[260px] truncate" title={c.email}>{c.email}</span>
                      </TableCell>
                      <TableCell>
                        {c.inLocal ? (
                          <Badge variant="success">In DB</Badge>
                        ) : (
                          <Badge variant="destructive">Only in Brevo</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-end gap-2 px-2 py-3">
              <select
                className="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <span className="text-sm">Page {page + 1} of {totalPages || 1}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function DbOnlyContactsTable({ title, emails }: { title: string; emails: string[] }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = [...emails].sort((a, b) => {
    const ea = a.toLowerCase();
    const eb = b.toLowerCase();
    if (ea < eb) return sortAsc ? -1 : 1;
    if (ea > eb) return sortAsc ? 1 : -1;
    return 0;
  });

  const paged = rowsPerPage > 0 ? sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : sorted;
  const total = emails.length;
  const totalPages = Math.ceil(emails.length / rowsPerPage);

  return (
    <AccordionItem value={`${slugify(title)}-dbonly`}>
      <AccordionTrigger className="text-sm font-semibold">
        {title} — {total} contact{total !== 1 ? 's' : ''} in DB only
      </AccordionTrigger>
      <AccordionContent>
        {emails.length === 0 ? (
          <p className="text-muted-foreground">No contacts are only in our database for this list.</p>
        ) : (
          <div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button className="flex items-center gap-1" onClick={() => setSortAsc(!sortAsc)}>
                        Email <IconArrowsSort className="h-3 w-3" />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paged.map((email, i) => (
                    <TableRow key={`${email}-${i}`}>
                      <TableCell>
                        <span className="block max-w-[260px] truncate" title={email}>{email}</span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-end gap-2 px-2 py-3">
              <select
                className="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
                value={rowsPerPage}
                onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}
              </select>
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <span className="text-sm">Page {page + 1} of {totalPages || 1}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

export function BrevoContactsSyncPage() {
  const { hasWriteAccess } = useAuth();
  const canWrite = hasWriteAccess('spar-mobile');
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [search, setSearch] = useState('');
  const [brevoRawResponse, setBrevoRawResponse] = useState<unknown | null>(null);
  const [syncStats, setSyncStats] = useState<{
    mode: 'single' | 'all';
    action: 'dcs' | 'roms' | 'stores' | 'all';
    syncedCount?: number;
    prunedTotal?: number;
    lists?: Array<{
      label: string;
      listId?: number;
      level?: number;
      syncedCount?: number;
      prunedCount?: number;
    }>;
  } | null>(null);
  const [remoteStatus, setRemoteStatus] = useState<RemoteStatusResponse | null>(null);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const [remoteError, setRemoteError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    action: 'dcs' | 'roms' | 'stores' | 'all' | null;
  }>({ open: false, action: null });

  const handlePreview = async () => {
    setPreviewError('');
    setPreview(null);
    setPreviewLoading(true);
    try {
      const res = await api.get<PreviewResponse>('/brevo/contacts-sync/preview');
      setPreview(res.data);
    } catch (err: any) {
      setPreviewError(err.response?.data?.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleLoadRemoteStatus = async () => {
    setRemoteError('');
    setRemoteLoading(true);
    try {
      const res = await api.get<RemoteStatusResponse>('/brevo/contacts-sync/remote');
      setRemoteStatus(res.data);
    } catch (err: any) {
      setRemoteError(err.response?.data?.message || 'Failed to load Brevo list status');
    } finally {
      setRemoteLoading(false);
    }
  };

  const handleSyncConfirm = () => {
    if (confirmDialog.action) {
      handleSync(confirmDialog.action);
      setConfirmDialog({ open: false, action: null });
    }
  };

  const handleSync = async (action: 'dcs' | 'roms' | 'stores' | 'all') => {
    setSyncMessage(null);
    setBrevoRawResponse(null);
    setSyncStats(null);
    setSyncing(action);
    try {
      const path = `/brevo/contacts-sync/${action}`;
      const res = await api.post(path);
      const data = res.data as {
        success?: boolean;
        message?: string;
        syncedCount?: number;
        listIds?: number[];
        prunedEmailsCount?: number;
        prunedEmailsTotal?: number;
        brevoResponse?: { status: number; data: unknown };
        byList?: Record<string, { syncedCount: number; listId: number; level: number; prunedEmailsCount?: number; brevoResponse?: { status: number; data: unknown } }>;
      } | undefined;

      if (data?.success === true) {
        const msg = data.message ?? 'Sync completed';
        setSyncMessage({ type: 'success', text: msg });

        if (action === 'all' && data.byList) {
          const lists: Array<{ label: string; listId?: number; level?: number; syncedCount?: number; prunedCount?: number }> = [];
          const dc = data.byList['dc'];
          const rom = data.byList['rom'];
          const store = data.byList['store'];
          if (dc) lists.push({ label: 'DC (list 9, level 1)', listId: dc.listId, level: dc.level, syncedCount: dc.syncedCount, prunedCount: dc.prunedEmailsCount ?? 0 });
          if (rom) lists.push({ label: 'ROMs (list 10, level 2)', listId: rom.listId, level: rom.level, syncedCount: rom.syncedCount, prunedCount: rom.prunedEmailsCount ?? 0 });
          if (store) lists.push({ label: 'Stores (list 11, level 3)', listId: store.listId, level: store.level, syncedCount: store.syncedCount, prunedCount: store.prunedEmailsCount ?? 0 });
          setSyncStats({ mode: 'all', action, syncedCount: data.syncedCount, prunedTotal: data.prunedEmailsTotal, lists });
        } else {
          setSyncStats({
            mode: 'single',
            action,
            syncedCount: data.syncedCount,
            prunedTotal: data.prunedEmailsCount ?? 0,
            lists: data.listIds?.map((listId) => ({
              label: action === 'dcs' ? `DC (list ${listId}, level 1)` : action === 'roms' ? `ROMs (list ${listId}, level 2)` : `Stores (list ${listId}, level 3)`,
              listId,
              syncedCount: data.syncedCount,
              prunedCount: data.prunedEmailsCount ?? 0,
            })) ?? [],
          });
        }

        if (data.brevoResponse) setBrevoRawResponse(data.brevoResponse);
        else if (data.byList) setBrevoRawResponse(data.byList);
        else setBrevoRawResponse(data);
      } else {
        setSyncMessage({ type: 'error', text: data?.message ?? 'Sync failed' });
      }
    } catch (err: any) {
      setSyncMessage({ type: 'error', text: err.response?.data?.message || 'Sync failed' });
    } finally {
      setSyncing(null);
    }
  };

  return (
    <div>
      <PageHeader
        title="SPAR Contacts Sync to Brevo"
        subtitle="This syncs sub distributor contacts from our database to Brevo lists (DC, ROM, Stores)."
      />

      <MainCard className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Our lists — Preview contacts</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This shows the contacts in our database that will be synced to Brevo (per list).
        </p>
        {previewError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {previewError}
              <button onClick={() => setPreviewError('')} className="text-sm underline ml-4">Dismiss</button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex items-center gap-4 flex-wrap mb-4">
          <Button variant="outline" onClick={handlePreview} disabled={previewLoading}>
            {previewLoading ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconFlask className="mr-2 h-4 w-4" />}
            {previewLoading ? 'Loading preview...' : 'Preview contacts'}
          </Button>
          {preview && (
            <Input
              className="max-w-xs"
              placeholder="Filter by email, name, DC/ROM/Store, role…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </div>

        {preview && (
          <Accordion multiple className="mt-2">
            <ContactTable title="DC (list 9)" contacts={preview.dc} search={search} />
            <ContactTable title="ROMs (list 10)" contacts={preview.rom} search={search} />
            <ContactTable title="Stores (list 11)" contacts={preview.store} search={search} />
          </Accordion>
        )}
      </MainCard>

      <MainCard className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Brevo lists — current state</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Fetched directly from Brevo. Rows marked &quot;Only in Brevo&quot; exist in Brevo but not in our database.
        </p>
        {remoteError && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {remoteError}
              <button onClick={() => setRemoteError('')} className="text-sm underline ml-4">Dismiss</button>
            </AlertDescription>
          </Alert>
        )}
        <Button variant="outline" onClick={handleLoadRemoteStatus} disabled={remoteLoading}>
          {remoteLoading ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconFlask className="mr-2 h-4 w-4" />}
          {remoteLoading ? 'Loading Brevo lists...' : 'Load Brevo lists'}
        </Button>

        {remoteStatus && (
          <div className="mt-4">
            <Accordion multiple defaultValue={[`${slugify('DC (list 9)')}-remote`, `${slugify('ROMs (list 10)')}-remote`, `${slugify('Stores (list 11)')}-remote`]}>
              <RemoteContactsTable title="DC (list 9)" contacts={remoteStatus.dc} />
              <RemoteContactsTable title="ROMs (list 10)" contacts={remoteStatus.rom} />
              <RemoteContactsTable title="Stores (list 11)" contacts={remoteStatus.store} />
            </Accordion>
            {remoteStatus.onlyInDb && (
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2">Contacts only in our database (not currently in Brevo)</h4>
                <Accordion multiple defaultValue={[`${slugify('DC (list 9)')}-dbonly`, `${slugify('ROMs (list 10)')}-dbonly`, `${slugify('Stores (list 11)')}-dbonly`]}>
                  <DbOnlyContactsTable title="DC (list 9)" emails={remoteStatus.onlyInDb.dc} />
                  <DbOnlyContactsTable title="ROMs (list 10)" emails={remoteStatus.onlyInDb.rom} />
                  <DbOnlyContactsTable title="Stores (list 11)" emails={remoteStatus.onlyInDb.store} />
                </Accordion>
              </div>
            )}
          </div>
        )}
      </MainCard>

      <MainCard>
        <h3 className="text-lg font-semibold mb-4">Sync to Brevo</h3>
        {syncMessage && (
          <Alert
            variant={syncMessage.type === 'error' ? 'destructive' : 'default'}
            className={cn('mb-4', syncMessage.type === 'success' && 'border-green-500 text-green-700')}
          >
            <AlertDescription className="flex items-center justify-between">
              {syncMessage.text}
              <button onClick={() => setSyncMessage(null)} className="text-sm underline ml-4">Dismiss</button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex gap-4 flex-wrap">
          <Button
            onClick={() => setConfirmDialog({ open: true, action: 'dcs' })}
            disabled={!!syncing || !canWrite}
            aria-busy={syncing === 'dcs'}
          >
            {syncing === 'dcs' ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCloudUpload className="mr-2 h-4 w-4" />}
            {syncing === 'dcs' ? 'Syncing DC (list 9)...' : 'Sync DC (list 9)'}
          </Button>
          <Button
            onClick={() => setConfirmDialog({ open: true, action: 'roms' })}
            disabled={!!syncing || !canWrite}
            aria-busy={syncing === 'roms'}
          >
            {syncing === 'roms' ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCloudUpload className="mr-2 h-4 w-4" />}
            {syncing === 'roms' ? 'Syncing ROMs (list 10)...' : 'Sync ROMs (list 10)'}
          </Button>
          <Button
            onClick={() => setConfirmDialog({ open: true, action: 'stores' })}
            disabled={!!syncing || !canWrite}
            aria-busy={syncing === 'stores'}
          >
            {syncing === 'stores' ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCloudUpload className="mr-2 h-4 w-4" />}
            {syncing === 'stores' ? 'Syncing Stores (list 11)...' : 'Sync Stores (list 11)'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => setConfirmDialog({ open: true, action: 'all' })}
            disabled={!!syncing || !canWrite}
            aria-busy={syncing === 'all'}
          >
            {syncing === 'all' ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCloudUpload className="mr-2 h-4 w-4" />}
            {syncing === 'all' ? 'Syncing all...' : 'Sync all'}
          </Button>
        </div>
        {!canWrite && (
          <Alert className="mt-4">
            <AlertDescription>
              You do not have write access to sync contacts. Contact your administrator for access.
            </AlertDescription>
          </Alert>
        )}
        {syncStats && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2">Sync summary</h4>
            {syncStats.mode === 'all' ? (
              <div className="ml-2">
                <p className="text-sm">
                  Total synced: {syncStats.syncedCount ?? 0} contact{(syncStats.syncedCount ?? 0) === 1 ? '' : 's'} across all lists. Pruned{' '}
                  {syncStats.prunedTotal ?? 0} Brevo contact{(syncStats.prunedTotal ?? 0) === 1 ? '' : 's'} not present in DB.
                </p>
                {syncStats.lists?.map((l) => (
                  <p key={l.label} className="text-sm">• {l.label}: synced {l.syncedCount ?? 0}, pruned {l.prunedCount ?? 0}</p>
                ))}
              </div>
            ) : (
              <div className="ml-2">
                {syncStats.lists && syncStats.lists.length > 0 ? (
                  syncStats.lists.map((l) => (
                    <p key={l.label} className="text-sm">
                      {l.label}: synced {l.syncedCount ?? 0}, pruned {l.prunedCount ?? 0} Brevo contact{(l.prunedCount ?? 0) === 1 ? '' : 's'} not present in DB.
                    </p>
                  ))
                ) : (
                  <p className="text-sm">
                    Synced {syncStats.syncedCount ?? 0} contacts. Pruned {syncStats.prunedTotal ?? 0} Brevo contact{(syncStats.prunedTotal ?? 0) === 1 ? '' : 's'} not present in DB.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
        {brevoRawResponse != null && (
          <div className="mt-4">
            <h4 className="text-sm font-semibold mb-2">Raw Brevo response</h4>
            <pre className="bg-muted rounded-md p-3 text-xs max-h-60 overflow-auto whitespace-pre-wrap break-words">
              {JSON.stringify(brevoRawResponse, null, 2)}
            </pre>
          </div>
        )}
      </MainCard>

      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Sync</DialogTitle>
            <DialogDescription>
              {confirmDialog.action === 'all' ? (
                'Are you sure you want to sync all contacts to Brevo? This will sync contacts to all three lists (DC, ROM, Stores) and remove any contacts from Brevo that are not present in our database.'
              ) : confirmDialog.action === 'dcs' ? (
                'Are you sure you want to sync contacts to the DC list (list 9)? This will update the Brevo list and remove any contacts that are not present in our database.'
              ) : confirmDialog.action === 'roms' ? (
                'Are you sure you want to sync contacts to the ROMs list (list 10)? This will update the Brevo list and remove any contacts that are not present in our database.'
              ) : confirmDialog.action === 'stores' ? (
                'Are you sure you want to sync contacts to the Stores list (list 11)? This will update the Brevo list and remove any contacts that are not present in our database.'
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: null })}>Cancel</Button>
            <Button onClick={handleSyncConfirm} disabled={!!syncing}>
              {syncing ? <IconLoader2 className="mr-2 h-4 w-4 animate-spin" /> : <IconCloudUpload className="mr-2 h-4 w-4" />}
              {syncing ? 'Syncing...' : 'Confirm Sync'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
